import { Component, OnInit, inject } from '@angular/core';
import { SortDescriptor, State, process } from '@progress/kendo-data-query';
import {
  GridDataResult,
  PageChangeEvent,
  GridModule,
} from '@progress/kendo-angular-grid';
import { UserManagementService } from '../user-management/user-management.service';
import {
  UntypedFormGroup,
  FormGroup,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AddRepComponent } from './add-rep/add-rep.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RepData } from '../../models/admin-models';
import { UtilityToolbarComponent } from '../../shared-components/utility-toolbar/utility-toolbar.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { AdminService } from '../admin.service';

@Component({
  selector: 'app-rep-management',
  templateUrl: './rep-management.component.html',
  styleUrls: ['./rep-management.component.scss'],
  imports: [
    UtilityToolbarComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    GridModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
  ],
})
export class RepManagementComponent implements OnInit {
  private userService = inject(UserManagementService);
  private adminService = inject(AdminService);
  dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);

  public pageSize = 15;
  public gridView: GridDataResult;
  noRecordMessage = 'Perform a search to find reps';
  searchForm: FormGroup;
  lastPage = false;
  openIndex: number;
  public multiple = false;
  public gridForm: FormGroup;
  public sort: SortDescriptor[] = [
    {
      field: 'repcode',
      dir: 'asc',
    },
  ];

  allUnits = [];

  unitData = [];

  repData: { repcodes: RepData[] } = { repcodes: [] };
  public state: State = {
    skip: 0,
    take: this.pageSize,
    sort: this.sort,
    filter: {
      logic: 'and',
      filters: [{ field: 'repcode', operator: 'contains', value: '' }],
    },
  };

  constructor() {
    this.adminService.getUnitList().subscribe({
      next: units => {
        this.allUnits = units['names'].map(r => r['name']);
        this.unitData = units['names'];
      },
      error: error => {
        alert(error);
      },
    });

    const formGroup = {};
    formGroup['repcode'] = new FormControl();
    formGroup['name'] = new FormControl();
    formGroup['unitname'] = new FormControl();
    formGroup['unitid'] = new FormControl();
    this.searchForm = new UntypedFormGroup(formGroup);
  }

  openSnackBar(message: string) {
    this._snackBar.open(message, '', {
      duration: 2000,
    });
  }

  ngOnInit(): void {}

  public pageChange({ skip, take }: PageChangeEvent): void {
    this.state['skip'] = skip;
    this.state['take'] = take;
    this.gridView = process(this.repData['repcodes'], this.state);
    if (skip + take >= this.repData['repcodes'].length && !this.lastPage) {
      this.searchReps();
    }
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.state['sort'] = this.sort;
    this.gridView = process(this.repData['repcodes'], this.state);
  }

  searchReps() {
    let start = 0;
    if (this.repData['repcodes']) {
      start = this.repData['repcodes'].length;
    }
    const data = this.searchForm.value;
    this.userService
      .searchReps(this.searchForm.value, start)
      .subscribe((reps: { repcodes: RepData[] }) => {
        this.noRecordMessage = 'No Reps Found';
        if (reps['repcodes'].length === 0) {
          this.lastPage = true;
        }
        if (this.repData['repcodes']) {
          this.repData['repcodes'] = this.repData['repcodes'].concat(
            reps['repcodes']
          );
        } else {
          this.repData = reps;
        }
        this.gridView = process(this.repData['repcodes'], this.state);
      });
  }

  openCreateRepDialog(index = null) {
    const dialogRef = this.dialog.open(AddRepComponent, {
      panelClass: 'rep-dialog',
      data: { headline: 'Add Rep' },
    });
    dialogRef.componentInstance.unitData = this.unitData;
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.addRep(result['data']);
      }
    });
  }

  addRep(data) {
    this.userService.addRep(data).subscribe(() => {
      this.openSnackBar('Rep Created.');
      this.userService
        .searchReps({ repcode: data['repcode'] })
        .subscribe(reps => {
          this.resetSearch();
          this.noRecordMessage = 'No Reps Found';
          if (reps['repcodes'].length === 0) {
            this.lastPage = true;
          }
          if (this.repData['repcodes']) {
            this.repData['repcodes'] = this.repData['repcodes'].concat(
              reps['repcodes']
            );
          } else {
            this.repData = reps;
          }
          this.gridView = process(this.repData['repcodes'], this.state);
          if (this.repData['repcodes'].length > 0) {
            this.expandIndex(this.repData['repcodes'][0], 0);
          }
        });
    });
  }

  newSearch() {
    this.resetSearch();
    this.searchReps();
  }

  resetSearch() {
    this.repData = { repcodes: [] };
    this.state['skip'] = 0;
    this.lastPage = false;
  }

  clearSearch() {
    this.searchForm.reset();
  }

  dataStateChange(event) {}

  expandSelected({ dataItem, index }: any): boolean {
    if (dataItem['component']) {
      return index === dataItem['component'].openIndex;
    }
    return false;
  }

  public detailExpand(event: any): void {
    this.expandIndex(event['dataItem'], event['index']);
  }

  public expandIndex(data, index) {
    this.openIndex = index;
    data['component'] = this;
    this.gridForm = new UntypedFormGroup({
      repCode: new FormControl(data['repCode']),
      repName: new FormControl(data['repName']),
      UnitId: new FormControl(data['UnitId']),
      Active: new FormControl(data['Active']),
    });
  }

  public detailCollapse(event: any): void {
    this.openIndex = -1;
  }

  saveRep() {
    const data = this.gridForm.value;
    this.userService.updateRep(data).subscribe(() => {
      this.openSnackBar('Rep Saved');

      this.userService
        .searchReps({ repcode: data['repCode'] }, 0)
        .subscribe((reps: { repcodes: RepData[] }) => {
          if (reps['repcodes'].length) {
            const data = reps['repcodes'][0];
            for (let i = 0; i < this.repData['repcodes'].length; i++) {
              if (this.repData['repcodes'][i]['repCode'] === data['repCode']) {
                this.repData['repcodes'][i] = data;
                this.gridView = process(this.repData['repcodes'], this.state);
                this.expandIndex(this.repData['repcodes'][i], i);
                break;
              }
            }
          }
        });
    });
  }
}
